import React, { useState } from 'react';

interface UncertaintyItem {
  analyzer: string;
  severity: string;
  question: string;
  transaction_description?: string;
  transaction_amount?: number;
}

interface UncertaintyReport {
  items: UncertaintyItem[];
  total_count: number;
}

interface UncertaintyResolutionData {
  skipped: boolean;
  resolutions?: any[];
  total_resolved?: number;
  pass_count?: number;
  convergence_achieved?: boolean;
  processing_time_ms?: number;
}

interface UncertaintyPanelProps {
  uncertaintyReport?: UncertaintyReport;
  uncertaintyResolution?: UncertaintyResolutionData;
  processingTime?: {
    rust_ms?: number;
    uncertainty_resolution_ms?: number;
  };
}

const UncertaintyPanel: React.FC<UncertaintyPanelProps> = ({
  uncertaintyReport,
  uncertaintyResolution,
  processingTime
}) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandedAnalyzers, setExpandedAnalyzers] = useState<Set<string>>(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState<'all' | 'high' | 'needs_review'>('all');

  // Show if there are resolutions OR current uncertainties
  const hasResolutions = uncertaintyResolution && !uncertaintyResolution.skipped && (uncertaintyResolution.resolutions?.length || 0) > 0;
  const hasItems = uncertaintyReport && uncertaintyReport.items.length > 0;

  if (!hasResolutions && !hasItems) return null;

  const resolvedCount = uncertaintyResolution?.total_resolved || 0;
  const passCount = uncertaintyResolution?.pass_count || 0;
  const converged = uncertaintyResolution?.convergence_achieved || false;

  const toggleItem = (idx: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedItems(newExpanded);
  };

  const toggleAnalyzer = (analyzer: string) => {
    const newExpanded = new Set(expandedAnalyzers);
    if (newExpanded.has(analyzer)) {
      newExpanded.delete(analyzer);
    } else {
      newExpanded.add(analyzer);
    }
    setExpandedAnalyzers(newExpanded);
  };

  const resolutions = uncertaintyResolution?.resolutions || [];

  // Group resolutions by analyzer
  const groupedByAnalyzer = resolutions.reduce((acc, resolution, idx) => {
    const analyzer = resolution.original_uncertainty?.analyzer || 'unknown';
    if (!acc[analyzer]) acc[analyzer] = [];
    acc[analyzer].push({ resolution, originalIndex: idx });
    return acc;
  }, {} as Record<string, Array<{ resolution: any; originalIndex: number }>>);

  // Filter resolutions by confidence
  const filterByConfidence = (res: any) => {
    if (confidenceFilter === 'all') return true;
    if (confidenceFilter === 'high') return res.confidence >= 85;
    if (confidenceFilter === 'needs_review') return res.confidence < 70;
    return true;
  };

  // Apply filter to grouped data
  const filteredGroups = Object.entries(groupedByAnalyzer).reduce((acc, [analyzer, items]) => {
    const filtered = (items as Array<{ resolution: any; originalIndex: number }>).filter(item => filterByConfidence(item.resolution));
    if (filtered.length > 0) acc[analyzer] = filtered;
    return acc;
  }, {} as Record<string, Array<{ resolution: any; originalIndex: number }>>);

  return (
    <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg overflow-hidden mb-3">
      <div
        className="px-3 py-2 cursor-pointer hover:bg-gray-700/30 transition-all flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{converged ? '✓' : '⚠'}</span>
          <span className="text-xs font-semibold text-white">
            Uncertainties
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            converged
              ? 'bg-green-500/20 text-green-400'
              : 'bg-amber-500/20 text-amber-400'
          }`}>
            {converged ? `${resolvedCount} resolved` : `${uncertaintyReport?.total_count || 0} flagged`}
          </span>
          {passCount > 1 && (
            <span className="text-xs text-gray-400">• {passCount} passes</span>
          )}
        </div>
        <span className={`text-gray-400 text-xs transform transition-transform ${expanded ? 'rotate-180' : ''}`}>▼</span>
      </div>

      {expanded && (
        <div className="border-t border-gray-700/50 bg-gray-900/20">
          {/* Filter Dropdown */}
          <div className="px-3 py-2 border-b border-gray-700/50 flex items-center gap-2">
            <span className="text-xs text-gray-400">Filter:</span>
            <select
              value={confidenceFilter}
              onChange={(e) => setConfidenceFilter(e.target.value as any)}
              className="text-xs bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="all">All ({resolutions.length})</option>
              <option value="high">High Confidence ≥85% ({resolutions.filter(r => r.confidence >= 85).length})</option>
              <option value="needs_review">Needs Review &lt;70% ({resolutions.filter(r => r.confidence < 70).length})</option>
            </select>
          </div>

          {/* Grouped by Analyzer */}
          <div className="px-3 py-2 space-y-2">
            {Object.keys(filteredGroups).length > 0 ? (
              Object.entries(filteredGroups).map(([analyzer, items]) => {
                const isAnalyzerExpanded = expandedAnalyzers.has(analyzer);
                const analyzerName = analyzer.replace(/_/g, ' ');
                const avgConfidence = Math.round(items.reduce((sum, item) => sum + item.resolution.confidence, 0) / items.length);

                return (
                  <div key={analyzer} className="border border-gray-700/50 rounded bg-gray-800/30">
                    {/* Analyzer Group Header */}
                    <div
                      className="px-2 py-1.5 cursor-pointer hover:bg-gray-700/20 transition-all flex items-center justify-between"
                      onClick={() => toggleAnalyzer(analyzer)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-blue-400">{analyzerName}</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-400">{items.length} items</span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className={`text-xs ${avgConfidence >= 85 ? 'text-green-400' : avgConfidence >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
                          avg {avgConfidence}%
                        </span>
                      </div>
                      <span className={`text-gray-400 text-xs transform transition-transform ${isAnalyzerExpanded ? 'rotate-180' : ''}`}>▼</span>
                    </div>

                    {/* Analyzer Items */}
                    {isAnalyzerExpanded && (
                      <div className="border-t border-gray-700/50 space-y-1 p-2">
                        {items.map(({ resolution, originalIndex }) => {
                          const uncertainty = resolution.original_uncertainty;
                          const isExpanded = expandedItems.has(originalIndex);
                          const severityColor =
                            uncertainty.severity === 'High' ? 'text-red-400' :
                            uncertainty.severity === 'Medium' ? 'text-yellow-400' :
                            'text-blue-400';

                          return (
                            <div key={originalIndex} className="border border-gray-700/50 rounded bg-gray-800/50">
                              <div
                                className="px-2 py-1.5 cursor-pointer hover:bg-gray-700/30 transition-all"
                                onClick={(e) => { e.stopPropagation(); toggleItem(originalIndex); }}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <span className={`text-xs font-mono ${severityColor}`}>
                                      {uncertainty.severity === 'High' ? 'H' : uncertainty.severity === 'Medium' ? 'M' : 'L'}
                                    </span>
                                    <span className="text-xs text-white truncate flex-1">
                                      {uncertainty.transaction_description || uncertainty.question.substring(0, 60)}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {resolution.answer && (
                                      <span className="text-xs text-green-400 font-medium">
                                        {resolution.answer.replace(/_/g, ' ').substring(0, 20)}
                                      </span>
                                    )}
                                    <span className={`text-xs ${
                                      resolution.confidence >= 85 ? 'text-green-400' :
                                      resolution.confidence >= 70 ? 'text-yellow-400' :
                                      'text-red-400'
                                    }`}>
                                      {resolution.confidence}%
                                    </span>
                                    <span className={`text-gray-400 text-xs transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                                  </div>
                                </div>
                              </div>

                              {isExpanded && (
                                <div className="px-3 py-2 border-t border-gray-700/50 bg-gray-900/30 space-y-2">
                                  <div>
                                    <div className="text-xs text-gray-400 mb-1">Question</div>
                                    <div className="text-xs text-white">{uncertainty.question}</div>
                                  </div>

                                  {uncertainty.transaction_description && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-1">Transaction</div>
                                      <div className="flex justify-between items-start bg-gray-800/50 rounded px-2 py-1">
                                        <span className="text-xs text-gray-300">{uncertainty.transaction_description}</span>
                                        {uncertainty.transaction_amount !== undefined && (
                                          <span className="text-xs text-white font-mono ml-2">
                                            ${uncertainty.transaction_amount.toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  <div>
                                    <div className="text-xs text-gray-400 mb-1">Answer</div>
                                    <div className="text-xs text-green-400 bg-gray-800/50 rounded px-2 py-1">
                                      {resolution.answer ? resolution.answer.replace(/_/g, ' ') : 'No answer'}
                                    </div>
                                  </div>

                                  {resolution.reasoning && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-1">Reasoning</div>
                                      <div className="text-xs text-gray-300 bg-gray-800/50 rounded px-2 py-1">
                                        {resolution.reasoning}
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex items-center gap-3 text-xs text-gray-500">
                                    <span>Confidence: {resolution.confidence}%</span>
                                    <span>•</span>
                                    <span>Severity: {uncertainty.severity}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-gray-400 text-center py-2">
                No items match the current filter
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UncertaintyPanel;
